import { describeApiError } from '@brush/api-client';
import { Spinner } from '@brush/ui';
import { useState } from 'react';
import { usePlayer } from '../player';
import { useScreenViewed } from '../api';
import { useRatingStandings, useVisibleRatingBoards } from '../queries';
import {
  boardFreshness,
  offersBreakdown,
  openingBoardId,
  ownPosition,
  standingsState,
} from '../ratings/board';
import { RatingBreakdown } from './RatingBreakdown';

/**
 * §4.7's `Players` audience, reaching a screen — a room's rating boards, their published standings,
 * and §7's working behind the reader's own row.
 *
 * <b>This section did not exist until now, and its absence was deliberate rather than pending.</b> The
 * portal carried one until ratings Phase 4b: the legacy `RatingGroup`, tag-filtered participation with
 * no scoring at all, labelled *Tag groups* so that one word would not mean two different features to
 * two audiences. §13 retired that entity, and until the route was widened a section here would have
 * been a tab that 403s.
 *
 * <b>It reads the published snapshot and never `/compute`.</b> That route runs the engine and is
 * staff-only; one answers what a rating <i>does</i> say and the other what it <i>would</i> say, and
 * collapsing them is how a published standing starts moving under the person reading it.
 *
 * <b>The reader's own row is marked in signal orange, and nothing else on the screen is.</b> On this
 * side of the glass orange means "this is you" and never "act here", so the row tint is exactly what
 * it is supposed to say — and the affordance that opens a breakdown is a chalk chevron.
 */
export function RatingsView() {
  const { player, room, theme } = usePlayer();
  useScreenViewed('ratings', player?.roomId ?? null);

  const boardsQ = useVisibleRatingBoards(room?.id ?? null);
  const boards = boardsQ.data ?? [];

  const [chosenId, setChosenId] = useState<string | null>(null);

  // A stored choice wins while it still names a board on the list. A board can be closed or narrowed
  // between renders, and opening the section on an empty panel reads as a fault rather than as a
  // board that went.
  const activeId = openingBoardId(boards, chosenId);
  const active = boards.find((b) => b.id === activeId) ?? null;

  const standingsQ = useRatingStandings(activeId);
  const standings = standingsQ.data?.data ?? [];
  const published = standingsQ.data?.sequenceNumber ?? null;

  /*
   * WHICH OF FIVE THINGS THIS PANEL IS SHOWING — and the precedence is the point, not the list.
   *
   * There was no failure branch: the row count was checked first, so every failed read rendered
   * "Your room hasn't worked this rating out yet", which is a statement about the room written by a
   * crash. `standingsState` holds the ordering and the reasoning, with tests.
   */
  const panel = standingsState({
    isLoading: standingsQ.isLoading,
    failure: standingsQ.isError ? describeApiError(standingsQ.error) : null,
    sequenceNumber: published,
    rowCount: standings.length,
  });

  // §7. Whose breakdown is open — and only ever the reader's own, because that is the only row the
  // server will explain (ADR 0011 Decision 10).
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);

  // Derived rather than cleared: switching board must not leave a panel attached to a row that is
  // not in this board's standings, and there is no moment at which the two can disagree this way.
  const openId = standings.some((r) => r.playerId === openPlayerId) ? openPlayerId : null;

  const mine = ownPosition(standings, player?.id ?? null);
  const freshness = boardFreshness(active);

  const card = { background: theme.panel, border: `1px solid ${theme.border}` };

  if (boardsQ.isLoading) return <Spinner label="Loading ratings…" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Ratings</h1>

      {boards.length === 0 ? (
        <p style={card} className="rounded-xl p-8 text-center text-sm">
          {/*
           * Two causes and one sentence, deliberately: a room that runs no rating and a room whose
           * ratings are staff-only are the same thing from here, and naming the second would tell a
           * player about a board the room chose not to show them.
           */}
          <span style={{ color: theme.textMuted }}>
            Your room isn’t publishing any ratings at the moment.
          </span>
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {boards.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => {
                  setChosenId(b.id);
                  // A rank means nothing across two boards, so an expansion cannot survive the
                  // switch — it would be one player's working on a board they may not even be on.
                  setOpenPlayerId(null);
                }}
                className="rounded-lg px-3 py-2 text-sm font-medium transition"
                style={{
                  background: b.id === activeId ? `${theme.accent}1a` : theme.panel,
                  color: b.id === activeId ? theme.accent : theme.textMuted,
                  border: `1px solid ${b.id === activeId ? theme.accent : theme.border}`,
                }}
              >
                {b.name}
              </button>
            ))}
          </div>

          <section style={card} className="overflow-hidden rounded-xl">
            <header className="p-5" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <h2
                className="text-sm font-bold uppercase tracking-wider"
                style={{ color: theme.accent }}
              >
                {active?.name}
              </h2>

              {active?.description ? (
                <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
                  {active.description}
                </p>
              ) : null}

              {/* §2.2's anchor. A rating moves when the calendar moves, so this is the one fact the
                  figures below are meaningless without. */}
              {freshness === null ? null : (
                <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
                  {freshness}
                </p>
              )}

              {/*
               * §9.3's one sentence about the reader, and the thing they came back for. A column of
               * gaps on every row is arithmetic nobody reads; where *you* are is not.
               */}
              {mine === null ? null : (
                <p className="mt-2 text-xs" style={{ color: theme.accent }}>
                  You are {ordinal(mine.rank)}
                  {mine.isTied ? ' (tied)' : ''} of {mine.total} on {mine.displayRating}
                  {mine.isQualified
                    ? '.'
                    : mine.qualificationShortfall === null
                      ? ', provisionally.'
                      : `, provisionally — ${mine.qualificationShortfall}.`}
                </p>
              )}
            </header>

            {panel.kind === 'loading' ? (
              <Spinner label="Loading standings…" />
            ) : panel.kind === 'failed' ? (
              /*
               * A FAILURE SAYS SO, rather than borrowing the sentence below it.
               *
               * Chalk and never signal orange: on this ground orange means "this is you" and never
               * "something is wrong", so an orange notice here would be the one bug this palette
               * makes easy to write. `describeApiError` supplies the words — several of the refusals
               * reachable here are specific and actionable (a card revoked at this room, a board
               * whose visibility narrowed), and a generic line would throw that away.
               */
              <p
                className="p-8 text-center text-sm leading-relaxed"
                style={{ color: theme.textMuted }}
              >
                These standings couldn’t be loaded.
                <br />
                <span className="text-xs">{panel.message}</span>
              </p>
            ) : panel.kind !== 'rows' ? (
              // Two causes, two sentences. "No standings yet" alone tells a player nothing about
              // whether to keep checking back — and neither of them may stand in for a failure,
              // which is what the branch above is for.
              <p className="p-8 text-center text-sm" style={{ color: theme.textMuted }}>
                {panel.kind === 'unpublished'
                  ? 'Your room hasn’t worked this rating out yet.'
                  : 'Nobody has a rating on this board yet.'}
              </p>
            ) : (
              <ul>
                {standings.map((r) => {
                  const isMe = r.playerId === player?.id;
                  const canOpen = offersBreakdown(r.playerId, player?.id ?? null);
                  const isOpen = openId === r.playerId;

                  return (
                    <li
                      key={r.playerId}
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        background: isMe ? `${theme.accent}12` : 'transparent',
                      }}
                    >
                      {/*
                        THE ROW IS A CONTROL ONLY WHERE THERE IS SOMETHING BEHIND IT.
                        §7's working is the reader's own and nobody else's (ADR 0011 Decision 10) —
                        the server refuses any other row — so a chevron on a stranger's row would be
                        an affordance whose only answer is a refusal. The rest of the board is a
                        plain row, which is what it is: everybody's rank and rating, published.
                      */}
                      <Row
                        row={r}
                        isMe={isMe}
                        isOpen={isOpen}
                        onToggle={
                          canOpen
                            ? () => setOpenPlayerId(isOpen ? null : r.playerId)
                            : undefined
                        }
                      />

                      {isOpen && activeId !== null && published !== null ? (
                        <RatingBreakdown
                          boardId={activeId}
                          sequenceNumber={published}
                          playerId={r.playerId}
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * One standing.
 *
 * <b>A button where the reader can open it and a plain div where they cannot</b>, rather than a
 * disabled control — a greyed chevron on every other row says "not now" where the truth is "never",
 * which is §9.9's own rule for a pinned points system applied to a row.
 */
function Row({
  row,
  isMe,
  isOpen,
  onToggle,
}: {
  row: {
    rank: number;
    playerName: string;
    displayRating: number;
    eventsSelected: number;
    eventsEntered: number;
    isTied: boolean;
    isQualified: boolean;
  };
  isMe: boolean;
  isOpen: boolean;
  onToggle?: () => void;
}) {
  const { theme } = usePlayer();

  const body = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
          style={{
            background: row.rank === 1 ? theme.accent : theme.border,
            color: row.rank === 1 ? theme.bg : theme.text,
          }}
        >
          {/* The snapshot's rank, not the row number: tied players share a rank and the next one
              skips (1, 2, 2, 4). */}
          {row.rank}
        </span>
        <div className="min-w-0">
          <div className="truncate font-semibold">
            {row.playerName}
            {isMe ? (
              <span
                className="ml-2 text-xs uppercase tracking-wider"
                style={{ color: theme.accent }}
              >
                You
              </span>
            ) : null}
          </div>
          <div className="text-xs" style={{ color: theme.textMuted }}>
            {row.eventsSelected} of {row.eventsEntered} event
            {row.eventsEntered === 1 ? '' : 's'} counted
            {row.isTied ? ' · tied' : ''}
            {row.isQualified ? '' : ' · provisional'}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <div className="text-right">
          <div className="font-bold" style={{ color: theme.accent }}>
            {row.displayRating}
          </div>
          <div
            className="text-[10px] uppercase tracking-wider"
            style={{ color: theme.textMuted }}
          >
            rating
          </div>
        </div>
        {onToggle === undefined ? null : (
          <span
            aria-hidden
            className="text-xs transition-transform"
            style={{
              color: theme.textMuted,
              transform: isOpen ? 'rotate(90deg)' : 'none',
            }}
          >
            ▶
          </span>
        )}
      </div>
    </>
  );

  if (onToggle === undefined) {
    return <div className="flex items-center justify-between gap-3 px-5 py-3">{body}</div>;
  }

  return (
    <button
      type="button"
      aria-expanded={isOpen}
      onClick={onToggle}
      // The hover tint is white rather than the accent: on this ground signal orange means "this is
      // you", and lighting a row up in it as the pointer crosses would say that about whoever it
      // happened to be over.
      className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-white/5"
    >
      {body}
    </button>
  );
}

function ordinal(n: number): string {
  const suffix =
    n % 100 >= 11 && n % 100 <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';

  return `${n}${suffix}`;
}
